import React, { useEffect, useState, useRef } from 'react';
import './Profile.css';
import { auth, db } from '../../Firebaseconfig';
import { onAuthStateChanged, updateProfile } from 'firebase/auth';
import { arrayUnion, arrayRemove, doc, onSnapshot, setDoc, collection, query, orderBy, where, runTransaction, deleteDoc, getDoc } from 'firebase/firestore';
import imageCompression from 'browser-image-compression';
import { BiMessageDetail } from "react-icons/bi";
import { FiGift, FiHeart, FiMapPin, FiPlus, FiTrash2, FiUser, FiMail, FiPhone, FiClock, FiFileText, FiBriefcase, FiTrash } from "react-icons/fi";
import { IoWalletOutline, IoRestaurantOutline } from "react-icons/io5";
import { MdOutlineCreditCard, MdMoreHoriz } from "react-icons/md";
import { GiReceiveMoney } from "react-icons/gi";
import { Link } from "react-router-dom";

function PaymentMethods({ cards, userId }) {
  const [showAddCard, setShowAddCard] = useState(false);
  const [cardForm, setCardForm] = useState({ number: '', name: '', expiry: '', cvv: '' });
  const [cashMenuOpen, setCashMenuOpen] = useState(false);


  const handleAddCard = async () => {
    if (!cardForm.number || !cardForm.name || !cardForm.expiry || !cardForm.cvv) return;
    if (!userId) return;
    const last4 = cardForm.number.replace(/\s/g, '').slice(-4);
    const newCard = {
      id: `${Date.now()}`,
      last4,
      name: cardForm.name,
      expiry: cardForm.expiry,
      createdAt: new Date().toISOString()
    };
    try {
      await setDoc(doc(db, 'users', userId), {
        cards: arrayUnion(newCard)
      }, { merge: true });

      setCardForm({ number: '', name: '', expiry: '', cvv: '' });
      setShowAddCard(false);
    } catch (err) {
      console.error("Error adding card:", err);
      alert("Failed to save card: " + err.message);
    }
  };

  const handleRemoveCard = async (card) => {
    if (!userId) return;
    if (window.confirm("Are you sure you want to remove this card?")) {
      try {
        await setDoc(doc(db, 'users', userId), {
          cards: arrayRemove(card)
        }, { merge: true });
      } catch (err) {
        console.error("Error removing card:", err);
        alert("Failed to remove card: " + err.message);
      }
    }
  };

  const formatCardNumber = (val) => {
    return val.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
  };

  const formatExpiry = (val) => {
    const clean = val.replace(/\D/g, '').slice(0, 4);
    if (clean.length >= 3) return clean.slice(0, 2) + '/' + clean.slice(2);
    return clean;
  };

  return (
    <div className="payment-container">
      <h2 className="payment-main-title">Payment methods</h2>

      {/* Credit & Debit Cards */}
      <div className="payment-section">
        <h3 className="payment-section-title">Credit and debit cards</h3>

        {cards.map(card => (
          <div key={card.id} className="payment-item">
            <div className="payment-item-left">
              <div className="payment-icon-wrap card-icon-wrap">
                <MdOutlineCreditCard className="payment-method-icon" />
              </div>
              <span className="payment-item-label">•••• •••• •••• {card.last4}</span>
              <span className="payment-item-sub">Expires {card.expiry}</span>
            </div>
            <button
              type="button"
              className="payment-more-btn"
              onClick={() => handleRemoveCard(card)}
              title="Remove Card"
            >
              < FiTrash />
            </button>
          </div>
        ))}

        {!showAddCard ? (
          <button
            type="button"
            className="add-card-btn"
            onClick={() => setShowAddCard(true)}
          >
            <span className="add-card-plus">+</span>
            <span>Add new card</span>
          </button>
        ) : (
          <div className="add-card-form">
            <div className="card-form-row">
              <input
                className="card-input"
                placeholder="Card number"
                value={cardForm.number}
                onChange={e => setCardForm({ ...cardForm, number: formatCardNumber(e.target.value) })}
                maxLength={19}
                aria-label="Card number"
              />
            </div>
            <div className="card-form-row">
              <input
                className="card-input"
                placeholder="Cardholder name"
                value={cardForm.name}
                onChange={e => setCardForm({ ...cardForm, name: e.target.value })}
                aria-label="Cardholder name"
              />
            </div>
            <div className="card-form-row card-form-row--split">
              <input
                className="card-input"
                placeholder="MM/YY"
                value={cardForm.expiry}
                onChange={e => setCardForm({ ...cardForm, expiry: formatExpiry(e.target.value) })}
                maxLength={5}
                aria-label="Expiry date"
              />
              <input
                className="card-input"
                placeholder="CVV"
                value={cardForm.cvv}
                onChange={e => setCardForm({ ...cardForm, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                maxLength={4}
                aria-label="CVV"
              />
            </div>
            <div className="card-form-actions">
              <button type="button" className="card-cancel-btn" onClick={() => setShowAddCard(false)}>Cancel</button>
              <button type="button" className="card-save-btn" onClick={handleAddCard}>Add card</button>
            </div>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="payment-divider" />

      {/* Other Methods */}
      <div className="payment-section">
        <h3 className="payment-section-title">Other methods</h3>

        <div className="payment-item">
          <div className="payment-item-left">
            <div className="payment-icon-wrap cash-icon-wrap">
              <GiReceiveMoney className="payment-method-icon cash-method-icon" />
            </div>
            <span className="payment-item-label">Cash</span>
          </div>
          <div className="payment-more-wrapper">
            <button
              type="button"
              className="payment-more-btn"
              onClick={() => setCashMenuOpen(!cashMenuOpen)}
              aria-label="Cash options"
            >
              <MdMoreHoriz />
            </button>
            {cashMenuOpen && (
              <div className="payment-dropdown">
                <button type="button" className="payment-dropdown-item">Set as default</button>
                <button type="button" className="payment-dropdown-item payment-dropdown-item--danger">Remove</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Format Order Date Helper ───
const formatOrderDate = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// ─── Main Profile Component ──────────────────────────────────────────────────────────
function Profile() {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('Personal Info');
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  const bannerInputRef = useRef(null);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    businessName: '',
    description: '',
    businessLocation: '',
    contactNumber: '',
    openTime: '',
    closeTime: ''
  });
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [addressForm, setAddressForm] = useState({
    label: '',
    street: '',
    city: '',
    postalCode: '',
    country: ''
  });

  // tabs is defined after isCompany — see below
  const addresses = Array.isArray(userData?.addresses) ? userData.addresses : [];

  useEffect(() => {
    let unsubscribeDoc = null;
    let unsubscribeOrders = null;  // ðŸ‘ˆ add this at the top
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        const docRef = doc(db, 'users', user.uid);
        unsubscribeDoc = onSnapshot(docRef, (docSnap) => {
          if (docSnap.exists()) {
            setUserData(docSnap.data());
          }
          setLoading(false);
        }, (error) => {
          console.error("Error fetching user data:", error);
          setLoading(false);
        });

        // ðŸ‘‡ orders query goes here, inside if(user)
        const q = query(
          collection(db, 'orders'),
          where('customerId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );
        unsubscribeOrders = onSnapshot(q, (snap) => {
          setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
          setOrdersLoading(false);
        });

      } else {
        setUserData(null);
        setLoading(false);
        setOrders([]);        // ðŸ‘ˆ clear on logout
        setOrdersLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeDoc) unsubscribeDoc();
      if (unsubscribeOrders) unsubscribeOrders();  // ðŸ‘ˆ add this
    };
  }, []);

  const [redeemCodeInput, setRedeemCodeInput] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [adminPromoCodeText, setAdminPromoCodeText] = useState('');
  const [adminPromoType, setAdminPromoType] = useState('percentage');
  const [adminPromoValue, setAdminPromoValue] = useState('');
  const [adminPromoMaxUses, setAdminPromoMaxUses] = useState('');
  const [globalPromoCodes, setGlobalPromoCodes] = useState([]);

  useEffect(() => {
    if (userData?.role === 'admin' || userData?.role === 'superadmin') {
      const q = query(
        collection(db, 'promo_codes'),
        where('scope', '==', 'global')
      );
      const unsub = onSnapshot(q, (snapshot) => {
        setGlobalPromoCodes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      });
      return () => unsub();
    }
  }, [userData?.role]);

  const handleRedeemCode = async (e) => {
    e.preventDefault();
    if (!redeemCodeInput.trim()) return;
    const uppercaseCode = redeemCodeInput.trim().toUpperCase();
    const user = auth.currentUser;
    if (!user) {
      alert("Please log in to redeem promo codes.");
      return;
    }

    setRedeeming(true);
    try {
      await runTransaction(db, async (transaction) => {
        const codeDocRef = doc(db, 'promo_codes', uppercaseCode);
        const userDocRef = doc(db, 'users', user.uid);

        const codeSnap = await transaction.get(codeDocRef);
        if (!codeSnap.exists()) {
          throw new Error('This promo code does not exist.');
        }

        const userSnap = await transaction.get(userDocRef);
        if (!userSnap.exists()) {
          throw new Error('User data not found.');
        }

        const codeData = codeSnap.data();
        const userDataSnap = userSnap.data();

        if (!codeData.active) {
          throw new Error('This promo code is no longer active.');
        }

        const usedCount = codeData.usedCount || 0;
        const maxUses = codeData.maxUses || 0;
        if (usedCount >= maxUses) {
          throw new Error('This promo code has reached its usage limit.');
        }

        const userPromoCodes = userDataSnap.promoCodes || [];
        const alreadyRedeemed = userPromoCodes.some(c => c.code === uppercaseCode);
        if (alreadyRedeemed) {
          throw new Error('You have already redeemed this promo code.');
        }

        // 1. Update promo code counters
        const redeemedByArray = codeData.redeemedBy || [];
        transaction.update(codeDocRef, {
          usedCount: usedCount + 1,
          redeemedBy: [...redeemedByArray, user.uid]
        });

        // 2. Add to user's vouchers array
        const newVoucher = {
          code: codeData.code,
          type: codeData.type,
          value: Number(codeData.value),
          scope: codeData.scope,
          restaurantId: codeData.restaurantId || null,
          companyName: codeData.companyName || null,
          status: 'unused',
          redeemedAt: new Date().toISOString()
        };

        transaction.update(userDocRef, {
          promoCodes: [...userPromoCodes, newVoucher]
        });
      });

      alert(`Promo code ${uppercaseCode} successfully redeemed!`);
      setRedeemCodeInput('');
    } catch (err) {
      console.error("Redemption error:", err);
      alert(err.message);
    } finally {
      setRedeeming(false);
    }
  };

  const handleAdminCreatePromo = async (e) => {
    e.preventDefault();
    if (!adminPromoCodeText.trim() || !adminPromoValue || !adminPromoMaxUses) {
      alert("Please fill all fields.");
      return;
    }
    const cleanCode = adminPromoCodeText.trim().toUpperCase();

    if (!/^[A-Z0-9]+$/.test(cleanCode)) {
      alert("Promo code must be alphanumeric only.");
      return;
    }

    const codeDocRef = doc(db, 'promo_codes', cleanCode);

    try {
      const existSnap = await getDoc(codeDocRef);
      if (existSnap.exists()) {
        alert("This promo code already exists globally or for a restaurant.");
        return;
      }

      await setDoc(codeDocRef, {
        code: cleanCode,
        type: adminPromoType,
        value: parseFloat(adminPromoValue),
        scope: 'global',
        restaurantId: null,
        companyName: null,
        maxUses: parseInt(adminPromoMaxUses, 10),
        usedCount: 0,
        redeemedBy: [],
        active: true,
        createdAt: new Date().toISOString(),
        createdBy: auth.currentUser?.uid || 'admin'
      });

      setAdminPromoCodeText('');
      setAdminPromoValue('');
      setAdminPromoMaxUses('');
      alert("Global promo code created successfully!");
    } catch (err) {
      console.error("Error creating global code:", err);
      alert("Failed to create global promo code: " + err.message);
    }
  };

  const handleDeletePromo = async (codeId) => {
    if (window.confirm(`Are you sure you want to delete promo code ${codeId}?`)) {
      try {
        await deleteDoc(doc(db, 'promo_codes', codeId));
        alert("Promo code deleted successfully.");
      } catch (err) {
        console.error("Error deleting promo code:", err);
        alert("Failed to delete promo code: " + err.message);
      }
    }
  };

  const handleEditClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const user = auth.currentUser;
    if (!user) return;
    if (!file.type.startsWith('image/')) {
      alert('Please choose an image file.');
      e.target.value = '';
      return;
    }

    try {
      setUploading(true);

      const options = {
        maxSizeMB: 0.9,
        maxWidthOrHeight: 1024,
        useWebWorker: true
      };

      const compressedFile = await imageCompression(file, options);

      const base64String = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(compressedFile);
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });

      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, { photoURL: base64String }, { merge: true });

      setUserData((current) => ({ ...(current || {}), photoURL: base64String }));
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload profile picture: " + error.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeletePfp = async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateProfile(user, { photoURL: null });
      await setDoc(userDocRef, { photoURL: null }, { merge: true });
      setUserData((current) => ({ ...(current || {}), photoURL: null }));
    } catch (error) {
      console.error("Error deleting profile picture:", error);
      alert("Failed to delete profile picture: " + error.message);
    }
  };

  const isCompany = userData?.role === 'company' || userData?.role === 'merchant';

  const tabs = isCompany
    ? ['Restaurant Info', 'Payment Methods']
    : ['Personal Info', 'Payment Methods', 'Addresses', 'Order History', 'Redeem code'];

  const handleBannerEditClick = () => {
    if (bannerInputRef.current) bannerInputRef.current.click();
  };

  const handleBannerChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const user = auth.currentUser;
    if (!user) return;
    if (!file.type.startsWith('image/')) {
      alert('Please choose an image file.');
      e.target.value = '';
      return;
    }

    try {
      setUploadingBanner(true);

      const base64String = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });

      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, { bannerURL: base64String }, { merge: true });

      setUserData((current) => ({ ...(current || {}), bannerURL: base64String }));
    } catch (error) {
      console.error("Error uploading banner:", error);
      alert("Failed to upload banner picture: " + error.message);
    } finally {
      setUploadingBanner(false);
      e.target.value = '';
    }
  };

  const handleDeleteBanner = async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, { bannerURL: null }, { merge: true });
      setUserData((current) => ({ ...(current || {}), bannerURL: null }));
    } catch (error) {
      console.error("Error deleting banner picture:", error);
      alert("Failed to delete banner picture: " + error.message);
    }
  };

  const handleEditProfile = () => {
    setEditForm({
      firstName: userData?.firstName || '',
      lastName: userData?.lastName || '',
      phone: userData?.phone || '',
      businessName: userData?.businessName || '',
      description: userData?.description || '',
      businessLocation: userData?.businessLocation || '',
      contactNumber: userData?.contactNumber || userData?.phone || '',
      openTime: userData?.openTime || '',
      closeTime: userData?.closeTime || ''
    });
    setIsEditing(true);
  };

  const handleSaveProfile = async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const updateData = isCompany ? {
        businessName: editForm.businessName,
        description: editForm.description,
        businessLocation: editForm.businessLocation,
        contactNumber: editForm.contactNumber,
        phone: editForm.contactNumber,
        openTime: editForm.openTime,
        closeTime: editForm.closeTime
      } : {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        phone: editForm.phone
      };
      await setDoc(userDocRef, updateData, { merge: true });
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  const handleSaveAddress = async () => {
    const user = auth.currentUser;
    if (!user) return;

    if (!addressForm.street.trim() || !addressForm.city.trim()) {
      setAddressError('Street and city are required.');
      return;
    }

    try {
      setSavingAddress(true);
      setAddressError('');
      const newAddress = {
        id: `${Date.now()}`,
        label: addressForm.label.trim() || 'Home',
        street: addressForm.street.trim(),
        city: addressForm.city.trim(),
        postalCode: addressForm.postalCode.trim(),
        country: addressForm.country.trim(),
        createdAt: new Date().toISOString()
      };

      await setDoc(doc(db, 'users', user.uid), {
        addresses: arrayUnion(newAddress)
      }, { merge: true });

      setAddressForm({ label: '', street: '', city: '', postalCode: '', country: '' });
      setShowAddressForm(false);
    } catch (error) {
      console.error("Error saving address:", error);
      setAddressError('Could not save address. Please try again.');
    } finally {
      setSavingAddress(false);
    }
  };

  const handleRemoveAddress = async (address) => {
    const user = auth.currentUser;
    if (!user) return;
    if (window.confirm(`Are you sure you want to remove this address: "${address.label || address.street}"?`)) {
      try {
        await setDoc(doc(db, 'users', user.uid), {
          addresses: arrayRemove(address)
        }, { merge: true });
      } catch (err) {
        console.error("Error removing address:", err);
        alert("Failed to remove address: " + err.message);
      }
    }
  };

  if (loading) {
    return <div className="profile-loading">Loading...</div>;
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>Profile</h1>
        <button type="button" className="contact-support-btn">
          <BiMessageDetail className="support-icon" /><Link style={{ textDecoration: 'none', color: 'inherit' }} to={'/contact'}>Contact Support</Link>
        </button>
      </div>

      <div className="profile-tabs">
        {tabs.map(tab => (
          <span
            key={tab}
            className={`tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </span>
        ))}
      </div>

      {/* â”€â”€ Payment Methods Tab â”€â”€ */}
      {activeTab === 'Payment Methods' && (
        <PaymentMethods
          cards={Array.isArray(userData?.cards) ? userData.cards : []}
          userId={auth.currentUser?.uid}
        />
      )}

      {/* â”€â”€ Addresses Tab â”€â”€ */}
      {activeTab === 'Addresses' && (
        <div className="addresses-container">
          <div className="addresses-header">
            <div>
              <h2>Addresses</h2>
              <p>Manage where your orders should be delivered.</p>
            </div>
            {addresses.length > 0 && !showAddressForm && (
              <button
                type="button"
                className="address-add-btn"
                onClick={() => setShowAddressForm(true)}
              >
                <FiPlus className="address-add-icon" />
                Add new address
              </button>
            )}
          </div>

          {addresses.length === 0 && !showAddressForm && (
            <div className="address-empty-state">
              <div className="address-empty-icon">
                <FiMapPin />
              </div>
              <h3>You havenâ€™t saved any addresses yet</h3>
              <p>Add a new address easily below</p>
              <button
                type="button"
                className="address-primary-btn"
                onClick={() => setShowAddressForm(true)}
              >
                <FiPlus className="address-add-icon" />
                Add new address
              </button>
            </div>
          )}

          {addresses.length > 0 && (
            <div className="address-list">
              {addresses.map((address) => (
                <div className="address-item" key={address.id || `${address.street}-${address.city}`}>
                  <div className="address-icon-wrap">
                    <FiMapPin className="address-item-icon" />
                  </div>
                  <div className="address-text" style={{ flex: 1 }}>
                    <h3>{address.label || 'Address'}</h3>
                    <p>{address.street}</p>
                    <span>
                      {[address.postalCode, address.city, address.country].filter(Boolean).join(', ')}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="address-delete-btn"
                    onClick={() => handleRemoveAddress(address)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--red)',
                      cursor: 'pointer',
                      padding: '8px',
                      fontSize: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '6px',
                      transition: 'background 0.2s'
                    }}
                    onMouseOver={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                    onMouseOut={e => e.currentTarget.style.background = 'none'}
                    title="Delete Address"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              ))}
            </div>
          )}

          {showAddressForm && (
            <div className="address-form">
              <div className="address-form-row address-form-row--split">
                <input
                  className="address-input"
                  placeholder="Label, e.g. Home"
                  value={addressForm.label}
                  onChange={e => setAddressForm({ ...addressForm, label: e.target.value })}
                  aria-label="Address label"
                />
                <input
                  className="address-input"
                  placeholder="Street address"
                  value={addressForm.street}
                  onChange={e => setAddressForm({ ...addressForm, street: e.target.value })}
                  aria-label="Street address"
                />
              </div>
              <div className="address-form-row address-form-row--split">
                <input
                  className="address-input"
                  placeholder="City"
                  value={addressForm.city}
                  onChange={e => setAddressForm({ ...addressForm, city: e.target.value })}
                  aria-label="City"
                />
                <input
                  className="address-input"
                  placeholder="Postal code"
                  value={addressForm.postalCode}
                  onChange={e => setAddressForm({ ...addressForm, postalCode: e.target.value })}
                  aria-label="Postal code"
                />
              </div>
              <div className="address-form-row">
                <input
                  className="address-input"
                  placeholder="Country"
                  value={addressForm.country}
                  onChange={e => setAddressForm({ ...addressForm, country: e.target.value })}
                  aria-label="Country"
                />
              </div>
              {addressError && <p className="address-error">{addressError}</p>}
              <div className="address-form-actions">
                <button
                  type="button"
                  className="address-cancel-btn"
                  onClick={() => {
                    setShowAddressForm(false);
                    setAddressError('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="address-save-btn"
                  onClick={handleSaveAddress}
                  disabled={savingAddress}
                >
                  {savingAddress ? 'Saving...' : 'Save address'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Order History Tab ── */}
      {activeTab === 'Order History' && (
        <div className="orders-history-container">
          <div className="orders-history-header">
            <h2>Order History</h2>
            <p>Manage and track all your previous orders.</p>
          </div>
          {ordersLoading ? (
            <div className="orders-history-loading">
              <div className="profile-spinner" />
              <p>Loading orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="orders-history-empty">
              <IoRestaurantOutline className="empty-orders-icon" />
              <h3>No orders placed yet</h3>
              <p>When you order from restaurants, they will appear here.</p>
              <Link to="/products" className="explore-btn" style={{ textDecoration: 'none' }}>
                Order Now
              </Link>
            </div>
          ) : (
            <div className="orders-history-list">
              {orders.map(order => {
                const orderDate = formatOrderDate(order.createdAt);
                return (
                  <div key={order.id} className="order-history-card">
                    <div className="order-history-card-header">
                      <div className="order-history-restaurant-info">
                        <IoRestaurantOutline className="restaurant-icon" />
                        <div>
                          <h3>{order.companyName || 'Restaurant'}</h3>
                          <span className="order-date">{orderDate}</span>
                        </div>
                      </div>
                      <div className="order-history-total">
                        <span>Total:</span>
                        <strong>${parseFloat(order.total || 0).toFixed(2)}</strong>
                      </div>
                    </div>

                    <div className="order-history-card-body">
                      <div className="order-items-details">
                        <span className="items-title">Items ordered</span>
                        <p className="items-text">{order.itemsText || order.productName}</p>
                      </div>
                    </div>

                    <div className="order-history-card-footer">
                      <span className={`order-status-badge ${order.status || 'pending'}`}>
                        {order.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : 'Pending'}
                      </span>
                      {order.companyId && (
                        <Link to={`/company/${order.companyId}`} className="reorder-btn">
                          View Restaurant
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Personal Info / Restaurant Info Tab ── */}
      {(activeTab === 'Personal Info' || activeTab === 'Restaurant Info') && (
        <div className="profile-content">
          {isCompany && (
            <div className="company-banner-edit-wrapper">
              <input
                type="file"
                accept="image/*"
                ref={bannerInputRef}
                style={{ display: 'none' }}
                onChange={handleBannerChange}
                aria-label="Upload banner picture"
              />
              <div className="company-banner-preview">
                {userData?.bannerURL ? (
                  <img
                    src={userData.bannerURL}
                    alt="Banner"
                    className="company-banner-img"
                    style={{ opacity: uploadingBanner ? 0.5 : 1 }}
                  />
                ) : (
                  <div className="company-banner-placeholder">
                    <span>No banner photo uploaded</span>
                  </div>
                )}
                <div className="banner-overlay">
                  <button type="button" className="banner-overlay-btn" onClick={handleBannerEditClick}>
                    {uploadingBanner ? 'Uploading...' : 'Change Banner Photo'}
                  </button>
                  {userData?.bannerURL && (
                    <button type="button" className="banner-overlay-btn delete-btn" onClick={handleDeleteBanner}>
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="user-info-section">
            <div className="user-avatar-wrapper">
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
                aria-label="Upload profile picture"
              />
              <img
                src={userData?.photoURL || 'https://ui-avatars.com/api/?name=User&background=random'}
                alt="Profile"
                className="user-avatar"
                style={{ opacity: uploading ? 0.5 : 1 }}
              />
              <div className="avatar-overlay">
                <button type="button" className="overlay-btn" onClick={handleEditClick}>{uploading ? 'Uploading...' : 'Edit'}</button>
                <button type="button" className="overlay-btn" onClick={handleDeletePfp}>Delete</button>
              </div>
            </div>
            <div className="user-details-wrapper">
              <div className="details-header-row">
                <div className="profile-identity">
                  <h2>{isCompany ? (userData?.businessName || 'Business Name') : (userData ? `${userData.firstName} ${userData.lastName}` : 'Guest User')}</h2>
                  <span className="profile-role-badge">
                    {isCompany ? 'Restaurant Partner' : 'CraveDash Customer'}
                  </span>
                </div>
                <div className="details-actions">
                  {isEditing ? (
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button type="button" className="details-cancel-btn" onClick={() => setIsEditing(false)}>Cancel</button>
                      <button type="button" className="details-save-btn" onClick={handleSaveProfile}>Save Changes</button>
                    </div>
                  ) : (
                    <button type="button" className="details-edit-btn" onClick={handleEditProfile}>Edit Profile</button>
                  )}
                </div>
              </div>

              <div className="details-cards-grid">
                {isCompany ? (
                  <>
                    {/* Business Name */}
                    <div className={`details-card ${isEditing ? 'editing' : ''}`}>
                      <div className="details-card-icon-wrap">
                        <IoRestaurantOutline />
                      </div>
                      <div className="details-card-info">
                        <span className="details-card-label">Business Name</span>
                        {isEditing ? (
                          <input
                            className="details-card-input"
                            value={editForm.businessName}
                            onChange={e => setEditForm({ ...editForm, businessName: e.target.value })}
                            placeholder="e.g. Restorant Test"
                            aria-label="Business name"
                          />
                        ) : (
                          <span className="details-card-value">{userData?.businessName || '—'}</span>
                        )}
                      </div>
                    </div>

                    {/* Contact Number */}
                    <div className={`details-card ${isEditing ? 'editing' : ''}`}>
                      <div className="details-card-icon-wrap">
                        <FiPhone />
                      </div>
                      <div className="details-card-info">
                        <span className="details-card-label">Contact Number</span>
                        {isEditing ? (
                          <input
                            className="details-card-input"
                            value={editForm.contactNumber}
                            onChange={e => setEditForm({ ...editForm, contactNumber: e.target.value })}
                            placeholder="e.g. +383 45 678 678"
                            aria-label="Contact number"
                          />
                        ) : (
                          <span className="details-card-value">{userData?.contactNumber || userData?.phone || '—'}</span>
                        )}
                      </div>
                    </div>

                    {/* Email Address */}
                    <div className="details-card disabled">
                      <div className="details-card-icon-wrap">
                        <FiMail />
                      </div>
                      <div className="details-card-info">
                        <span className="details-card-label">Email Address</span>
                        <span className="details-card-value">{userData?.email || '—'}</span>
                      </div>
                    </div>

                    {/* Opening Time */}
                    <div className={`details-card ${isEditing ? 'editing' : ''}`}>
                      <div className="details-card-icon-wrap">
                        <FiClock />
                      </div>
                      <div className="details-card-info">
                        <span className="details-card-label">Opening Time</span>
                        {isEditing ? (
                          <input
                            className="details-card-input"
                            value={editForm.openTime}
                            onChange={e => setEditForm({ ...editForm, openTime: e.target.value })}
                            placeholder="e.g. 09:00"
                            aria-label="Opening time"
                          />
                        ) : (
                          <span className="details-card-value">{userData?.openTime || '—'}</span>
                        )}
                      </div>
                    </div>

                    {/* Closing Time */}
                    <div className={`details-card ${isEditing ? 'editing' : ''}`}>
                      <div className="details-card-icon-wrap">
                        <FiClock />
                      </div>
                      <div className="details-card-info">
                        <span className="details-card-label">Closing Time</span>
                        {isEditing ? (
                          <input
                            className="details-card-input"
                            value={editForm.closeTime}
                            onChange={e => setEditForm({ ...editForm, closeTime: e.target.value })}
                            placeholder="e.g. 18:00"
                            aria-label="Closing time"
                          />
                        ) : (
                          <span className="details-card-value">{userData?.closeTime || '—'}</span>
                        )}
                      </div>
                    </div>

                    {/* Business Location */}
                    <div className={`details-card ${isEditing ? 'editing' : ''}`}>
                      <div className="details-card-icon-wrap">
                        <FiMapPin />
                      </div>
                      <div className="details-card-info">
                        <span className="details-card-label">Business Location</span>
                        {isEditing ? (
                          <input
                            className="details-card-input"
                            value={editForm.businessLocation}
                            onChange={e => setEditForm({ ...editForm, businessLocation: e.target.value })}
                            placeholder="e.g. Prishtina, Kosovo"
                            aria-label="Business location"
                          />
                        ) : (
                          <span className="details-card-value">{userData?.businessLocation || '—'}</span>
                        )}
                      </div>
                    </div>

                    {/* Description - Spanning full width */}
                    <div className={`details-card full-width ${isEditing ? 'editing' : ''}`}>
                      <div className="details-card-icon-wrap">
                        <FiFileText />
                      </div>
                      <div className="details-card-info">
                        <span className="details-card-label">About Restaurant</span>
                        {isEditing ? (
                          <textarea
                            className="details-card-textarea"
                            value={editForm.description}
                            onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                            placeholder="Tell customers about your restaurant, specialities, history..."
                            aria-label="Restaurant description"
                          />
                        ) : (
                          <span className="details-card-value description-text">{userData?.description || 'No description provided.'}</span>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* First Name */}
                    <div className={`details-card ${isEditing ? 'editing' : ''}`}>
                      <div className="details-card-icon-wrap">
                        <FiUser />
                      </div>
                      <div className="details-card-info">
                        <span className="details-card-label">First Name</span>
                        {isEditing ? (
                          <input
                            className="details-card-input"
                            value={editForm.firstName}
                            onChange={e => setEditForm({ ...editForm, firstName: e.target.value })}
                            placeholder="First Name"
                            aria-label="First name"
                          />
                        ) : (
                          <span className="details-card-value">{userData?.firstName || '—'}</span>
                        )}
                      </div>
                    </div>

                    {/* Last Name */}
                    <div className={`details-card ${isEditing ? 'editing' : ''}`}>
                      <div className="details-card-icon-wrap">
                        <FiUser />
                      </div>
                      <div className="details-card-info">
                        <span className="details-card-label">Last Name</span>
                        {isEditing ? (
                          <input
                            className="details-card-input"
                            value={editForm.lastName}
                            onChange={e => setEditForm({ ...editForm, lastName: e.target.value })}
                            placeholder="Last Name"
                            aria-label="Last name"
                          />
                        ) : (
                          <span className="details-card-value">{userData?.lastName || '—'}</span>
                        )}
                      </div>
                    </div>

                    {/* Contact Number */}
                    <div className={`details-card ${isEditing ? 'editing' : ''}`}>
                      <div className="details-card-icon-wrap">
                        <FiPhone />
                      </div>
                      <div className="details-card-info">
                        <span className="details-card-label">Phone Number</span>
                        {isEditing ? (
                          <input
                            className="details-card-input"
                            value={editForm.phone}
                            onChange={e => setEditForm({ ...editForm, phone: e.target.value })}
                            placeholder="e.g. +383 45 678 678"
                            aria-label="Phone number"
                          />
                        ) : (
                          <span className="details-card-value">{userData?.phone || '—'}</span>
                        )}
                      </div>
                    </div>

                    {/* Email Address */}
                    <div className="details-card disabled">
                      <div className="details-card-icon-wrap">
                        <FiMail />
                      </div>
                      <div className="details-card-info">
                        <span className="details-card-label">Email Address</span>
                        <span className="details-card-value">{userData?.email || '—'}</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {!isCompany && <div className="cards-section">
            <div className="gift-card card">
              <div className="card-header">
                <div className="card-title">
                  <FiGift className="card-icon gift-icon" />
                  <h3>CraveDash gift cards</h3>
                </div>
              </div>
              <p className="card-text">
                Surprise your friends or yourself. You can use gift cards to pay for your orders at any participating restaurant.
              </p>
              <div className="card-actions">
                <button type="button" className="action-btn buy-btn">Buy gift card &rarr;</button>
                <button type="button" className="action-btn view-btn">View gift cards</button>
              </div>
            </div>

            <div className="credits-card card">
              <div className="card-header">
                <div className="card-title">
                  <IoWalletOutline className="card-icon wallet-icon" />
                  <h3>CraveDash credits</h3>
                </div>
                <span className="balance-amount">0.00€</span>
              </div>
              <p className="card-text">
                Your digital wallet balance. You can use credits to pay for your orders instantly.
              </p>
              <div className="card-actions">
                <button type="button" className="action-btn view-btn">View credits &rarr;</button>
              </div>
            </div>
          </div>}

          {!isCompany && <div className="favorites-section">
            <div className="favorites-header">
              <div className="favorites-title-area">
                <h2>Your favorites</h2>
                <p>You'll find your favorite restaurants and stores here. You can add favorites by tapping the heart icon on any venue page.</p>
              </div>
              <button type="button" className="favourite-btn">
                <FiHeart className="heart-icon" /> Favourite
              </button>
            </div>
            <div className="empty-state">
              <div className="empty-icon-wrapper">
                <IoRestaurantOutline className="empty-icon" />
              </div>
              <h3>Nothing here yet</h3>
              <p>Start exploring the best flavors in your city and<br />save them for quick access later.</p>
              <Link to="/products"><button type="button" className="explore-btn">Explore Restaurants</button></Link>
            </div>
          </div>}
        </div>
      )}

      {/* ── Redeem code Tab ── */}
      {activeTab === 'Redeem code' && (
        <div className="redeem-container">
          <div className="redeem-header">
            <h2>Redeem Promo Code</h2>
            <p>Enter a promo code to add a voucher to your account for your next order.</p>
          </div>

          <form onSubmit={handleRedeemCode} className="redeem-form">
            <input
              type="text"
              placeholder="ENTER CODE (e.g. SAVE10)"
              value={redeemCodeInput}
              onChange={e => setRedeemCodeInput(e.target.value.toUpperCase())}
              className="redeem-input"
              maxLength={20}
              required
            />
            <button type="submit" disabled={redeeming} className="redeem-submit-btn">
              {redeeming ? 'Redeeming...' : 'Redeem Code'}
            </button>
          </form>

          <div className="vouchers-section">
            <h3>Your Vouchers</h3>
            {(!userData?.promoCodes || userData.promoCodes.length === 0) ? (
              <div className="vouchers-empty-state">
                <FiGift className="vouchers-empty-icon" />
                <p className="no-vouchers-text">You don't have any redeemed vouchers yet.</p>
              </div>
            ) : (
              <div className="vouchers-grid">
                {userData.promoCodes.map((voucher, idx) => (
                  <div key={idx} className={`voucher-card ${voucher.status}`}>
                    <div className="voucher-card-badge">
                      <FiGift className="voucher-gift-icon" />
                    </div>
                    <div className="voucher-details">
                      <div className="voucher-header-info">
                        <span className="voucher-code">{voucher.code}</span>
                        <span className={`voucher-status-badge ${voucher.status}`}>
                          {voucher.status}
                        </span>
                      </div>
                      <p className="voucher-discount">
                        {voucher.type === 'percentage'
                          ? `${voucher.value}% OFF`
                          : `$${parseFloat(voucher.value).toFixed(2)} OFF`}
                      </p>
                      <p className="voucher-scope">
                        {voucher.scope === 'global'
                          ? 'Valid for all restaurants'
                          : `Only valid at ${voucher.companyName || 'selected restaurant'}`}
                      </p>
                      {voucher.redeemedAt && (
                        <span className="voucher-date">
                          Redeemed on {new Date(voucher.redeemedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Admin panel for creating global promo codes */}
          {(userData?.role === 'admin' || userData?.role === 'superadmin') && (
            <div className="admin-promo-section">
              <div className="admin-promo-header">
                <h3>Admin Panel: Manage Global Promo Codes</h3>
                <p>Create and delete discount codes that apply globally across all restaurants.</p>
              </div>

              <div className="admin-promo-layout">
                <form onSubmit={handleAdminCreatePromo} className="admin-promo-form">
                  <h4>Create Global Code</h4>
                  <div className="admin-form-group">
                    <label htmlFor="adminCodeInput">Promo Code</label>
                    <input
                      id="adminCodeInput"
                      type="text"
                      placeholder="GLOBAL20"
                      value={adminPromoCodeText}
                      onChange={e => setAdminPromoCodeText(e.target.value.toUpperCase())}
                      required
                    />
                  </div>
                  <div className="admin-form-group">
                    <label htmlFor="adminTypeSelect">Discount Type</label>
                    <select id="adminTypeSelect" value={adminPromoType} onChange={e => setAdminPromoType(e.target.value)}>
                      <option value="percentage">Percentage (%)</option>
                      <option value="value">Fixed Amount ($)</option>
                    </select>
                  </div>
                  <div className="admin-form-group">
                    <label htmlFor="adminValueInput">Value</label>
                    <input
                      id="adminValueInput"
                      type="number"
                      placeholder="e.g. 10 or 15"
                      value={adminPromoValue}
                      onChange={e => setAdminPromoValue(e.target.value)}
                      min="0.01"
                      step="any"
                      required
                    />
                  </div>
                  <div className="admin-form-group">
                    <label htmlFor="adminUsesInput">Max Uses</label>
                    <input
                      id="adminUsesInput"
                      type="number"
                      placeholder="e.g. 100"
                      value={adminPromoMaxUses}
                      onChange={e => setAdminPromoMaxUses(e.target.value)}
                      min="1"
                      required
                    />
                  </div>
                  <button type="submit" className="admin-create-btn">Create Global Code</button>
                </form>

                <div className="admin-promo-list">
                  <h4>Global Promo Codes</h4>
                  {globalPromoCodes.length === 0 ? (
                    <p className="no-codes-text">No global promo codes created yet.</p>
                  ) : (
                    <div className="admin-codes-table-wrapper">
                      <table className="admin-codes-table">
                        <thead>
                          <tr>
                            <th>Code</th>
                            <th>Type</th>
                            <th>Value</th>
                            <th>Usage</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {globalPromoCodes.map(code => (
                            <tr key={code.id}>
                              <td className="code-cell">{code.code}</td>
                              <td>{code.type === 'percentage' ? 'Percent' : 'Voucher'}</td>
                              <td>{code.type === 'percentage' ? `${code.value}%` : `$${code.value}`}</td>
                              <td>{code.usedCount} / {code.maxUses}</td>
                              <td>
                                <button
                                  type="button"
                                  onClick={() => handleDeletePromo(code.id)}
                                  className="admin-delete-code-btn"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Placeholder tabs ── */}
      {!['Personal Info', 'Payment Methods', 'Addresses', 'Order History', 'Redeem code'].includes(activeTab) && (
        <div className="tab-placeholder">
          <p>{activeTab} — coming soon</p>
        </div>
      )}
    </div>
  );
}

export default Profile;
